import app from '../../admin/app';
import DashboardWidget from './DashboardWidget';
import isExtensionEnabled from '../utils/isExtensionEnabled';
import Link from '../../common/components/Link';
import Icon from '../../common/components/Icon';

/**
 * Mirrors Extension::nameToId() from PHP.
 * "flarum/mentions"      → "flarum-mentions"
 * "flarum/flarum-ext-suspend" → "flarum-suspend"
 * "some-vendor/foo"      → "some-vendor-foo"
 */
function packageNameToExtensionId(packageName) {
  const [vendor, pkg] = packageName.split('/');
  const stripped = pkg.replace(/^flarum-ext-/, '').replace(/^flarum-/, '');
  return "".concat(vendor, "-").concat(stripped);
}
export default class ExtensionsWidget extends DashboardWidget {
  className() {
    return 'ExtensionsWidget';
  }
  content() {
    const abandoned = this.abandonedExtensions();
    const suggested = this.suggestedExtensions();
    const disabled = this.disabledExtensions();
    return [m("h3", {
      className: "ExtensionsWidget-title"
    }, m(Icon, {
      name: "fas fa-puzzle-piece"
    }), app.translator.trans('core.admin.extensions-health-widget.title')), m("div", {
      className: "ExtensionsWidget-list"
    }, this.renderSection('abandoned', abandoned, this.renderAbandonedItem.bind(this)), this.renderSection('suggested', suggested, this.renderSuggestedItem.bind(this)), this.renderDisabledSection(disabled))];
  }
  renderSection(type, items, renderItem) {
    const isHealthSection = type === 'abandoned' || type === 'suggested';
    return m("div", {
      className: "ExtensionsWidget-section ExtensionsWidget-section--".concat(type).concat(items.length ? ' ExtensionsWidget-section--has-items' : '')
    }, m("h4", {
      className: "ExtensionsWidget-sectionHeading"
    }, app.translator.trans("core.admin.extensions-health-widget.section_".concat(type, "_heading")), !!items.length && m("span", {
      className: "ExtensionsWidget-sectionCount"
    }, items.length)), m("p", {
      className: "ExtensionsWidget-sectionHelp"
    }, app.translator.trans("core.admin.extensions-health-widget.section_".concat(type, "_help"))), items.length ? m("ul", {
      className: "ExtensionsWidget-itemList"
    }, items.map(renderItem)) : m("p", {
      className: "ExtensionsWidget-sectionEmpty".concat(isHealthSection ? ' ExtensionsWidget-sectionEmpty--ok' : '')
    }, isHealthSection && m(Icon, {
      name: "fas fa-check-circle",
      className: "ExtensionsWidget-okIcon"
    }), app.translator.trans("core.admin.extensions-health-widget.section_".concat(type, "_empty"))));
  }
  renderDisabledSection(extensions) {
    return m("div", {
      className: "ExtensionsWidget-section ExtensionsWidget-section--disabled"
    }, m("h4", {
      className: "ExtensionsWidget-sectionHeading"
    }, app.translator.trans('core.admin.extensions-health-widget.section_disabled_heading'), !!extensions.length && m("span", {
      className: "ExtensionsWidget-sectionCount"
    }, extensions.length)), m("p", {
      className: "ExtensionsWidget-sectionHelp"
    }, app.translator.trans('core.admin.extensions-health-widget.section_disabled_help')), extensions.length ? m("ul", {
      className: "ExtensionsWidget-disabledGrid"
    }, extensions.map(this.renderDisabledItem.bind(this))) : m("p", {
      className: "ExtensionsWidget-sectionEmpty"
    }, app.translator.trans('core.admin.extensions-health-widget.section_disabled_empty')));
  }
  renderAbandonedItem(item) {
    const {
      extension
    } = item;
    const hasReplacement = typeof extension.abandoned === 'string';
    const title = extension.extra['flarum-extension'].title;
    return m("li", {
      className: "ExtensionsWidget-item ExtensionsWidget-item--".concat(hasReplacement ? 'danger' : 'warning')
    }, m(Link, {
      className: "ExtensionsWidget-itemLink",
      href: app.route('extension', {
        id: extension.id
      })
    }, m("span", {
      className: "ExtensionIcon ExtensionsWidget-itemIcon",
      style: extension.icon
    }, !!extension.icon && m(Icon, {
      name: extension.icon.name
    })), m("span", {
      className: "ExtensionsWidget-itemBody"
    }, m("span", {
      className: "ExtensionsWidget-itemName"
    }, title, m(Icon, {
      name: "fas fa-".concat(hasReplacement ? 'exclamation-circle' : 'exclamation-triangle'),
      className: "ExtensionsWidget-itemIndicator"
    })), m("span", {
      className: "ExtensionsWidget-itemDetail"
    }, hasReplacement ? app.translator.trans('core.admin.extensions-health-widget.abandoned_with_replacement', {
      replacement: m("code", null, extension.abandoned)
    }) : app.translator.trans('core.admin.extensions-health-widget.abandoned_no_replacement')))));
  }
  renderSuggestedItem(item) {
    const {
      packageId,
      description,
      suggestedBy
    } = item;
    const packagistUrl = "https://packagist.org/packages/".concat(packageId);
    return m("li", {
      className: "ExtensionsWidget-item ExtensionsWidget-item--info"
    }, m("a", {
      className: "ExtensionsWidget-itemLink",
      href: packagistUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("span", {
      className: "ExtensionsWidget-itemBody"
    }, m("span", {
      className: "ExtensionsWidget-itemName"
    }, m("code", null, packageId), m(Icon, {
      name: "fas fa-external-link-alt",
      className: "ExtensionsWidget-externalIcon"
    })), m("span", {
      className: "ExtensionsWidget-itemDetail"
    }, app.translator.trans('core.admin.extensions-health-widget.suggested_by', {
      extension: m("strong", null, suggestedBy)
    }), description ? " \u2014 ".concat(description) : ''))));
  }
  renderDisabledItem(extension) {
    const title = extension.extra['flarum-extension'].title;
    return m("li", {
      className: "ExtensionsWidget-disabledItem"
    }, m(Link, {
      href: app.route('extension', {
        id: extension.id
      })
    }, m("span", {
      className: "ExtensionIcon ExtensionsWidget-disabledIcon",
      style: extension.icon
    }, !!extension.icon && m(Icon, {
      name: extension.icon.name
    })), m("span", {
      className: "ExtensionsWidget-disabledName"
    }, title)));
  }
  abandonedExtensions() {
    return Object.values(app.data.extensions).filter(ext => ext.abandoned).map(extension => ({
      extension
    }));
  }
  suggestedExtensions() {
    var _app$data$installedPa;
    // Authoritative list of all installed composer packages (including non-extension libraries)
    const installedPackageNames = new Set((_app$data$installedPa = app.data.installedPackages) != null ? _app$data$installedPa : []);
    const suggestions = [];
    const seen = new Set();
    for (const ext of Object.values(app.data.extensions)) {
      if (!ext.suggest) continue;
      for (const [packageId, description] of Object.entries(ext.suggest)) {
        // Only surface vendor/package style entries (skip PHP ext-* etc.)
        if (!packageId.includes('/')) continue;
        if (seen.has(packageId)) continue;

        // Skip if already installed (as any composer package)
        if (installedPackageNames.has(packageId)) continue;
        seen.add(packageId);
        suggestions.push({
          packageId,
          description,
          suggestedBy: ext.extra['flarum-extension'].title
        });
      }
    }
    return suggestions;
  }
  disabledExtensions() {
    return Object.values(app.data.extensions).filter(ext => !isExtensionEnabled(ext.id) && !ext.abandoned);
  }
}
flarum.reg.add('core', 'admin/components/ExtensionsWidget', ExtensionsWidget);